import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RRule } from 'rrule';

@Component({
  selector: 'app-e-invoice-recurrence-editor-component',
  templateUrl: './e-invoice-recurrence-editor-component.component.html',
  styleUrls: ['./e-invoice-recurrence-editor-component.component.css']
})
export class EInvoiceRecurrenceEditorComponentComponent implements OnInit {
  @Input() existingRRule: string;
  @Output() rruleGenerated: EventEmitter<string> = new EventEmitter<string>();

  recurrenceForm: FormGroup;
  recurrencePatternOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' } // Add the "YEARLY" option
  ];
  recurrenceDaysOptions = [
    { value: 'MO', label: 'Monday' },
    { value: 'TU', label: 'Tuesday' },
    { value: 'WE', label: 'Wednesday' },
    { value: 'TH', label: 'Thursday' },
    { value: 'FR', label: 'Friday' },
    { value: 'SA', label: 'Saturday' },
    { value: 'SU', label: 'Sunday' }
  ];
  recurrenceMonthDaysOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  yearlyMonthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit() {
    this.initForm();
    if (this.existingRRule) {
      this.bindExistingRRule();
    }
  }

  initForm() {
    this.recurrenceForm = this.formBuilder.group({
      recurrencePattern: ['daily', Validators.required],
      recurrenceDays: [[]],
      recurrenceMonthDays: [[]],
      endOrCount: ['end'], // Default to 'End Date'
      endDate: [null],
      recurrenceCount: [null],
      yearlyMonth: [null], // Add yearly month FormControl
      yearlyDay: [null]    // Add yearly day FormControl
    });

    this.recurrenceForm.valueChanges.subscribe(() => {
      this.generateRRule();
    });
  }

  bindExistingRRule() {
    this.existingRRule = this.existingRRule.replace(/;$/, "");
    const rrule = RRule.fromString(this.existingRRule);
    const freq = this.getFrequencyString(rrule.options.freq);
    const endOrCount = rrule.options.until ? 'end' : 'count';

    this.recurrenceForm.patchValue({
      recurrencePattern: freq,
      endOrCount: endOrCount, // Set the endOrCount based on the presence of the 'until' property
      endDate: rrule.options.until || null,
      recurrenceCount: rrule.options.count || null,
      yearlyMonth: null, // Reset yearlyMonth
      yearlyDay: null    // Reset yearlyDay
    });

    console.log("options",rrule.options);
    if (freq === 'weekly') {
      const byweekday = rrule.options.byweekday.map((day) => this.getWeekdayString(day));
      this.recurrenceForm.patchValue({ recurrenceDays: byweekday });
    } else if (freq === 'monthly') {
      const bymonthday = rrule.options.bymonthday;
      this.recurrenceForm.patchValue({
        recurrenceMonthDays: bymonthday,
      });
    } else if (freq === 'yearly') {
      // Handle yearly pattern with BYMONTH and BYMONTHDAY
      const bymonth = rrule.options.bymonth;
      const bymonthday = rrule.options.bymonthday;

      if (bymonth && bymonthday) {
        this.recurrenceForm.patchValue({ yearlyMonth: bymonth[0], yearlyDay: bymonthday[0] });
      }
    }
  }

  generateRRule() {
    if (this.recurrenceForm.invalid) {
      return;
    }

    const pattern = this.recurrenceForm.value.recurrencePattern;
    const endOrCount = this.recurrenceForm.value.endOrCount;
    const endDate = this.recurrenceForm.value.endDate;
    const count = this.recurrenceForm.value.recurrenceCount;

    let rruleOptions: any = {
      freq: this.getFrequencyNumber(pattern),
      // until: endDate ? new Date(endDate) : undefined,
      // count: count || undefined
    };

    if (endOrCount === 'end') {
      rruleOptions.until = endDate ? new Date(endDate) : undefined;
    } else if (endOrCount === 'count') {
      rruleOptions.count = count || undefined;
    }

    if (pattern === 'weekly') {
      if (this.recurrenceForm.value.recurrenceDays) {
        const days = this.recurrenceForm.value.recurrenceDays.map((day: string) => this.getWeekdayNumber(day));
        rruleOptions.byweekday = [...days];
      }
    } else if (pattern === 'monthly') {
      if (this.recurrenceForm.value.recurrenceMonthDays) {
        const monthDays = this.recurrenceForm.value.recurrenceMonthDays;
        rruleOptions.bymonthday = monthDays;
        // const days = this.recurrenceForm.value.recurrenceDays.map((day: string) => this.getWeekdayNumber(day));
        // rruleOptions.byweekday = [...days];
      }
    } else if (pattern === 'yearly') {
      // Handle yearly pattern with user-specified month and day
      const yearlyMonth = this.recurrenceForm.value.yearlyMonth;
      const yearlyDay = this.recurrenceForm.value.yearlyDay;

      if (yearlyMonth && yearlyDay) {
        rruleOptions.bymonth = yearlyMonth;
        rruleOptions.bymonthday = yearlyDay;
      }
    }

    const rrule = new RRule(rruleOptions);
    const rruleString = rrule.toString();

    this.rruleGenerated.emit(rruleString.replace(/^RRULE:/, ""));
  }

  getFrequencyString(freq: number): string {
    switch (freq) {
      case RRule.DAILY:
        return 'daily';
      case RRule.WEEKLY:
        return 'weekly';
      case RRule.MONTHLY:
        return 'monthly';
      case RRule.YEARLY:
        return 'yearly';
      // Add cases for other frequencies if needed
      default:
        return '';
    }
  }

  getFrequencyNumber(freq: string): number {
    switch (freq) {
      case 'daily':
        return RRule.DAILY;
      case 'weekly':
        return RRule.WEEKLY;
      case 'monthly':
        return RRule.MONTHLY;
      case 'yearly':
        return RRule.YEARLY;
      // Add cases for other frequencies if needed
      default:
        return RRule.DAILY;
    }
  }

  getWeekdayNumber(day: any) {
    switch (day) {
      case 'SU' || 7:
        return RRule.SU;
      case 'MO' || 0:
        return RRule.MO;
      case 'TU' || 1:
        return RRule.TU;
      case 'WE' || 2:
        return RRule.WE;
      case 'TH' || 3:
        return RRule.TH;
      case 'FR' || 4:
        return RRule.FR;
      case 'SA' || 5:
        return RRule.SA;
      default:
        return 0;
    }
  }

  getWeekdayString(day: any) {
    switch (day) {
      case 7:
        return 'SU';
      case  0:
        return 'MO';
      case 1:
        return 'TU';
      case 2:
        return 'WE';
      case 3:
        return 'TH';
      case 4:
        return 'FR';
      case 5:
        return 'SA';
      default:
        return 0;
    }
  }
}
